// TanStack Query hooks for the AI clip suggestions written by the clips_ai_recommendation
// stage: the list for one recording (clip composer) and the reviewer's dismiss/restore.
// Accepting a suggestion is the normal create-clips call with `suggestion_id` on the range.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { ClipSuggestionList, ClipSuggestionRead } from "./types";

export function useClipSuggestions(recordingId: number | string) {
  return useQuery({
    queryKey: ["clip-suggestions", String(recordingId)],
    queryFn: () =>
      apiFetch<ClipSuggestionList>(`/api/recordings/${recordingId}/clip-suggestions`),
  });
}

function useDecision(recordingId: number | string, action: "dismiss" | "restore") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      apiFetch<ClipSuggestionRead>(`/api/clip-suggestions/${id}/${action}`, {
        method: "POST",
        body: JSON.stringify({ note: note ?? null }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clip-suggestions", String(recordingId)] });
      qc.invalidateQueries({ queryKey: ["recording", String(recordingId)] });
    },
  });
}

export const useDismissSuggestion = (recordingId: number | string) =>
  useDecision(recordingId, "dismiss");
export const useRestoreSuggestion = (recordingId: number | string) =>
  useDecision(recordingId, "restore");

// Human-readable labels for the stage's "why there are no suggestions" keys.
const ERROR_LABEL: Record<string, string> = {
  disabled: "AI suggestions are switched off on the server",
  no_api_key: "no OpenRouter key configured",
  too_short: "recording is shorter than the minimum clip length",
  no_prepared_frames: "no frames were cached for the model",
  no_catalog: "the task catalog is empty",
  no_sheets: "frames could not be rendered for the model",
  daily_cap: "daily model-call cap reached — reprocess tomorrow to get suggestions",
  all_calls_failed: "the vision model did not answer — reprocess to retry",
  no_calls: "no model calls were made",
};

export function suggestionErrorLabel(error: string | null | undefined): string | null {
  if (!error) return null;
  return ERROR_LABEL[error] ?? error;
}
